/**
 * Header Component - AWLP Design System
 * Top navigation bar with logo, notifications, settings, and user dropdown
 */

import { ChevronDown, LogOut, Menu, Settings, User as UserIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import authService from '../../../services/api/authService';
import { useAuthStore } from '../../../stores/authStore';
import { useUIStore } from '../../../stores/uiStore';
import { StoredImage } from '../../ui/StoredImage';
import { NotificationBell } from './NotificationBell';

const Container = styled.header`
  position: sticky;
  top: 0;
  height: ${({ theme }) => theme.layout.header.height};
  background: ${({ theme }) => theme.layout.header.background};
  color: ${({ theme }) => theme.colors.text.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing[6]};
  z-index: ${({ theme }) => theme.zIndex.sticky};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const HamburgerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.primary.lightSky};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Logo = styled.img`
  height: 30px;
  width: auto;
  display: block;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const IconButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.primary.lightSky};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const UserSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.white};
`;

const UserOrg = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary.lightSky};
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing[2]};
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  z-index: ${({ theme }) => theme.zIndex.dropdown};
`;

const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-align: left;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const MOBILE_BREAKPOINT = 768;

// Initials text rendered inside StoredImage when no profile picture is set.
const HeaderAvatarInitials = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.white};
`;

const getInitials = (firstName?: string, lastName?: string): string => {
  const f = firstName?.charAt(0) ?? '';
  const l = lastName?.charAt(0) ?? '';
  return `${f}${l}`.toUpperCase() || '?';
};

export const Header = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { user, refreshToken, beginLogout } = useAuthStore();
  const { toggleSidebar, toggleMobileSidebar } = useUIStore();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authService.logout(refreshToken ?? undefined);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear the local platform session (beginLogout also flags isLoggingOut so
      // ProtectedRoute renders nothing instead of navigating to /login — see its
      // doc comment for why that avoids a login-flash-then-bounce-to-Entra
      // glitch), then full-page redirect through Entra's end-session endpoint so
      // the Entra SSO cookie is also cleared — otherwise the next sign-in
      // silently re-authenticates the same account. Entra returns the browser to
      // /login afterwards.
      beginLogout();
      window.location.assign(authService.entraLogoutUrl('/login'));
    }
  };

  const handleProfile = () => {
    setIsDropdownOpen(false);
    navigate('/settings');
  };

  const handleHamburgerClick = () => {
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      toggleMobileSidebar();
    } else {
      toggleSidebar();
    }
  };

  return (
    <Container>
      <LeftSection>
        <HamburgerButton onClick={handleHamburgerClick} aria-label="Toggle sidebar">
          <Menu />
        </HamburgerButton>
        <Logo src="/assets/logo-small.png" alt="ABA Insights" />
      </LeftSection>

      <RightSection>
        <NotificationBell />
        <IconButton aria-label="Settings" onClick={() => navigate('/settings')}>
          <Settings />
        </IconButton>

        <UserSection onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <UserInfo>
            <UserName>
              {user?.firstName} {user?.lastName}
            </UserName>
            <UserOrg>{user?.organizationName || 'Organization'}</UserOrg>
          </UserInfo>
          <StoredImage
              src={user?.profilePic}
              alt={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'User avatar'}
              size="md"
              fallback={
                <HeaderAvatarInitials>
                  {getInitials(user?.firstName, user?.lastName)}
                </HeaderAvatarInitials>
              }
            />
          <ChevronDown size={16} />

          <Dropdown $isOpen={isDropdownOpen}>
            <DropdownItem onClick={handleProfile}>
              <UserIcon />
              Profile
            </DropdownItem>
            <DropdownItem onClick={handleLogout}>
              <LogOut />
              Logout
            </DropdownItem>
          </Dropdown>
        </UserSection>
      </RightSection>
    </Container>
  );
};

export default Header;
